import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

interface TrailerModalProps {
  movieId: number;
  mediaType: "movie" | "tv";
  title: string;
  open: boolean;
  onClose: () => void;
}

const TrailerModal = ({ movieId, mediaType, title, open, onClose }: TrailerModalProps) => {
  const { t } = useTranslation();
  const [trailerUrl, setTrailerUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(false);
    setTrailerUrl(null);

    const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
    const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

    fetch(
      `https://${projectId}.supabase.co/functions/v1/tmdb-trailer?id=${movieId}&type=${mediaType}`,
      {
        headers: {
          'Authorization': `Bearer ${anonKey}`,
          'apikey': anonKey,
        },
      }
    )
      .then((res) => res.json())
      .then((data) => {
        if (data.trailer?.url) {
          setTrailerUrl(data.trailer.url);
        } else {
          setError(true);
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [open, movieId, mediaType]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-background/90 backdrop-blur-md flex items-center justify-center px-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className="w-full max-w-lg bg-card border border-border rounded-2xl overflow-hidden shadow-card"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <h3 className="font-display text-sm font-bold text-foreground truncate pr-4">{title} — {t("trailerModal.trailer")}</h3>
              <button onClick={onClose} className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center shrink-0">
                <X className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            </div>

            <div className="aspect-video bg-secondary flex items-center justify-center">
              {loading && <Loader2 className="w-8 h-8 text-primary animate-spin" />}
              {error && (
                <div className="text-center p-6">
                  <span className="text-3xl block mb-2">😕</span>
                  <p className="text-sm text-muted-foreground">{t("trailerModal.noTrailer")}</p>
                </div>
              )}
              {trailerUrl && (
                <iframe
                  src={trailerUrl}
                  className="w-full h-full"
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                  title={`${title} trailer`}
                />
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TrailerModal;
